import { useEffect, useState } from 'react';
import { Modal, Radio } from 'antd';
import type { ButtonProps, RadioChangeEvent } from 'antd';

import { tpoOptionTestId } from '../../config/testIds';

interface ViewSelectOption<T extends string> {
    label: string;
    value: T;
}

interface ViewSelectModalProps<T extends string> {
    open: boolean;
    title: string;
    label: string;
    options: ReadonlyArray<ViewSelectOption<T>>;
    initialValue: T;
    onCancel: () => void;
    onApply: (value: T) => void;
    /** Modal id for QA locators. Options and footer buttons derive from it. */
    testId?: string;
}

function ViewSelectModal<T extends string>({
    open,
    title,
    label,
    options,
    initialValue,
    onCancel,
    onApply,
    testId,
}: ViewSelectModalProps<T>) {
    const [draft, setDraft] = useState<T>(initialValue);

    useEffect(() => {
        if (open) setDraft(initialValue);
    }, [open, initialValue]);

    const handleChange = (e: RadioChangeEvent) => setDraft(e.target.value as T);

    return (
        <Modal
            title={title}
            open={open}
            width={480}
            onCancel={onCancel}
            onOk={() => onApply(draft)}
            okText="Apply"
            cancelText="Cancel"
            destroyOnClose
            okButtonProps={testId ? ({ 'data-testid': `${testId}_apply_btn` } as ButtonProps) : undefined}
            cancelButtonProps={testId ? ({ 'data-testid': `${testId}_cancel_btn` } as ButtonProps) : undefined}
        >
            <div className="flex flex-col gap-y-3" data-testid={testId}>
                <p className="text-sm text-[#52525C]">{label}</p>
                <Radio.Group value={draft} onChange={handleChange}>
                    <div className="flex flex-col gap-y-3">
                        {options.map((opt) => (
                            <Radio
                                key={opt.value}
                                value={opt.value}
                                data-testid={testId ? tpoOptionTestId(testId, opt.value) : undefined}
                            >
                                {opt.label}
                            </Radio>
                        ))}
                    </div>
                </Radio.Group>
            </div>
        </Modal>
    );
}

ViewSelectModal.displayName = 'ViewSelectModal';
export default ViewSelectModal;
